import React, { useEffect, useState, useCallback } from 'react';
import { API_BASE as API_URL } from '../lib/apiBase';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { MultiSelect } from '../components/MultiSelect';
import { toast } from 'sonner';
import {
  Plus, Trash2, Pencil, ArrowUp, ArrowDown, Table2, Users, Loader2, GripVertical, Save, Eye, Lock, Unlock
} from 'lucide-react';

const getHeaders = () => {
  const token = localStorage.getItem('hrms-token');
  return token ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' };
};

const BUCKETS = [
  { value: 'earning', label: 'Earning' },
  { value: 'deduction', label: 'Deduction' },
  { value: 'info', label: 'Info (not added to totals)' },
];
const SOURCES = [
  { value: 'fixed', label: 'Fixed amount' },
  { value: 'manual', label: 'Per-employee amount' },
  { value: 'formula', label: 'Percentage of…' },
  { value: 'field', label: 'From payroll field' },
  { value: 'none', label: 'Not applicable (hide column)' },
];

const blankHead = () => ({
  label: '', bucket: 'earning', source_type: 'field', field_key: '',
  formula_base: 'gross_earned', formula_percent: 0, default_amount: 0, prorate: false,
});

// Demo employee (mirrors the backend's first preview profile) so the builder can show a
// LIVE value for each column the instant you change a %, base, or fixed amount.
const DEMO = (() => {
  const d = { present_days: 31, monthly_days: 31, basic: 42000, agp: 7000, da: 38000,
    hra: 4000, cla: 800, hod_allowance: 0, special_allowance: 2000, arrears: 0,
    pf: 1800, professional_tax: 200, esic: 0, tds: 2500, advance: 0, electricity: 150,
    local_mart: 100, bus_charges: 500, staff_quarters: 0 };
  d.basic_da = d.basic + d.da;
  d.ctc_basic = d.basic; d.ctc_da = d.da; d.ctc_hra = d.hra;
  d.ctc_gross_basic = d.basic + d.agp;
  d.gross_earned = d.basic + d.agp + d.da + d.hra + d.cla + d.hod_allowance + d.special_allowance + d.arrears;
  d.ctc_gross = d.gross_earned;
  d.total_deductions = 0; d.net = 0;
  return d;
})();

// One column's live value. Uses a real employee's figures (src) when one is selected,
// else the demo profile. Fixed/manual with no amount fall back to a sample (demo only).
const liveValue = (h, src, isReal) => {
  const st = h.source_type;
  if (st === 'none') return null;
  const s = src || DEMO;
  if (st === 'field') return Number(s[h.field_key] || 0);
  if (st === 'formula') {
    const base = Number(s[h.formula_base || 'gross_earned'] || 0);
    return Math.round(base * (parseFloat(h.formula_percent) || 0) / 100 * 100) / 100;
  }
  const amt = parseFloat(h.default_amount) || 0;
  if (amt) return amt;
  return isReal ? 0 : (h.bucket === 'earning' ? 1500 : 300);
};
const money = (n) => Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function SalaryTemplates() {
  const [templates, setTemplates] = useState([]);
  const [meta, setMeta] = useState({ fields: [], formula_bases: [], appointment_types: [], entities: [] });
  const [loading, setLoading] = useState(true);

  // builder dialog
  const [editing, setEditing] = useState(null);           // template being edited (or new)
  const [form, setForm] = useState(null);                 // { name, description, appointment_types, heads[], is_active }
  const [saving, setSaving] = useState(false);

  // per-employee amounts dialog
  const [valuesFor, setValuesFor] = useState(null);       // template
  const [gridHeads, setGridHeads] = useState([]);
  const [gridRows, setGridRows] = useState([]);
  const [gridLoading, setGridLoading] = useState(false);
  const [gridSaving, setGridSaving] = useState(false);

  // preview dialog
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewTitle, setPreviewTitle] = useState('');
  const [previewCtx, setPreviewCtx] = useState(null);      // { heads, templateId }
  const [previewEmp, setPreviewEmp] = useState('__demo');  // employee_id driving the preview

  // real-employee picker (shared by builder live values + preview)
  const [sampleEmps, setSampleEmps] = useState([]);
  const [empQuery, setEmpQuery] = useState('');
  const [builderEmp, setBuilderEmp] = useState(null);      // { employee_id, name, designation }
  const [builderFv, setBuilderFv] = useState(null);        // real field values for live column figures
  const [empLabels, setEmpLabels] = useState({});          // employee_id -> display label (survives search filtering)

  const builderOpen = !!form;

  const fetchSampleEmps = useCallback(async (query = '') => {
    try {
      const qs = query ? `?q=${encodeURIComponent(query)}` : '';
      const res = await fetch(`${API_URL}/api/salary-templates/sample-employees${qs}`, { headers: getHeaders(), credentials: 'include' });
      const d = await res.json();
      const list = Array.isArray(d) ? d : [];
      setSampleEmps(list);
      setEmpLabels(prev => { const n = { ...prev }; list.forEach(e => { n[e.employee_id] = `${e.name}${e.designation ? ` — ${e.designation}` : ''}`; }); return n; });
    } catch { setSampleEmps([]); }
  }, []);

  // Options for an employee select: always include the currently-selected one,
  // even when the search filter has excluded it from sampleEmps.
  const pickerOptions = (selectedId) => {
    const opts = sampleEmps.map(e => ({ id: e.employee_id, label: `${e.name}${e.designation ? ` — ${e.designation}` : ''}` }));
    if (selectedId && selectedId !== '__demo' && !opts.some(o => o.id === selectedId)) {
      opts.unshift({ id: selectedId, label: empLabels[selectedId] || selectedId });
    }
    return opts;
  };

  useEffect(() => {
    if (!builderOpen && !previewOpen) return;
    const t = setTimeout(() => fetchSampleEmps(empQuery), 300);
    return () => clearTimeout(t);
  }, [empQuery, builderOpen, previewOpen, fetchSampleEmps]);

  const pickBuilderEmp = async (empId) => {
    if (!empId || empId === '__demo') { setBuilderEmp(null); setBuilderFv(null); return; }
    try {
      const res = await fetch(`${API_URL}/api/salary-templates/employee-fv?employee_id=${encodeURIComponent(empId)}`, { headers: getHeaders(), credentials: 'include' });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Failed to load employee');
      setBuilderEmp({ employee_id: empId, name: d.name, designation: d.designation });
      setBuilderFv(d.fv);
      setEmpLabels(prev => ({ ...prev, [empId]: `${d.name}${d.designation ? ` — ${d.designation}` : ''}` }));
    } catch (e) { toast.error(e.message); }
  };

  const cleanedHeads = (heads) => heads.map(h => ({
    ...h,
    formula_percent: h.formula_percent === '' || h.formula_percent == null ? 0 : (parseFloat(h.formula_percent) || 0),
    default_amount: h.default_amount === '' || h.default_amount == null ? 0 : (parseFloat(h.default_amount) || 0),
  }));

  const runPreview = async (heads, templateId, employeeIds) => {
    setPreviewData(null); setPreviewLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/salary-templates/preview`, {
        method: 'POST', headers: getHeaders(), credentials: 'include',
        body: JSON.stringify({ heads: cleanedHeads(heads), employee_ids: employeeIds || [], template_id: templateId || null }),
      });
      const d = await res.json();
      if (!res.ok) {
        const msg = Array.isArray(d.detail) ? (d.detail[0]?.msg || 'Invalid') : (d.detail || 'Preview failed');
        throw new Error(msg);
      }
      setPreviewData(d);
    } catch (e) { toast.error(e.message); setPreviewOpen(false); } finally { setPreviewLoading(false); }
  };

  const openPreview = (heads, title, templateId = null, initialEmp = '__demo') => {
    const list = (heads || []).filter(h => (h.label || '').trim());
    if (!list.length) { toast.error('Add at least one column to preview'); return; }
    setPreviewTitle(title || 'Salary Sheet Preview');
    setPreviewCtx({ heads: list, templateId });
    setPreviewEmp(initialEmp);
    setPreviewOpen(true);
    runPreview(list, templateId, initialEmp === '__demo' ? [] : [initialEmp]);
  };

  const changePreviewEmp = (val) => {
    setPreviewEmp(val);
    if (previewCtx) runPreview(previewCtx.heads, previewCtx.templateId, val === '__demo' ? [] : [val]);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, m] = await Promise.all([
        fetch(`${API_URL}/api/salary-templates`, { headers: getHeaders(), credentials: 'include' }).then(r => r.json()),
        fetch(`${API_URL}/api/salary-templates/meta`, { headers: getHeaders(), credentials: 'include' }).then(r => r.json()),
      ]);
      setTemplates(Array.isArray(t) ? t : []);
      setMeta(m && m.fields ? m : { fields: [], formula_bases: [], appointment_types: [], entities: [] });
    } catch (e) {
      toast.error('Failed to load salary templates');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setBuilderEmp(null); setBuilderFv(null); setEmpQuery('');
    setEditing({ isNew: true });
    setForm({ name: '', description: '', entity_ids: [], appointment_types: [], heads: [blankHead()], is_active: true, locked: false });
  };
  const openEdit = (t) => {
    setBuilderEmp(null); setBuilderFv(null); setEmpQuery('');
    setEditing(t);
    setForm({
      name: t.name || '', description: t.description || '',
      entity_ids: t.entity_ids || [],
      appointment_types: t.appointment_types || [],
      heads: (t.heads || []).map(h => ({ ...h })),
      is_active: t.is_active !== false,
      locked: !!t.locked,
    });
  };

  const setHead = (i, patch) => setForm(f => ({ ...f, heads: f.heads.map((h, idx) => idx === i ? { ...h, ...patch } : h) }));
  const addHead = () => setForm(f => ({ ...f, heads: [...f.heads, blankHead()] }));
  const removeHead = (i) => setForm(f => ({ ...f, heads: f.heads.filter((_, idx) => idx !== i) }));
  const moveHead = (i, dir) => setForm(f => {
    const j = i + dir;
    if (j < 0 || j >= f.heads.length) return f;
    const arr = [...f.heads];
    [arr[i], arr[j]] = [arr[j], arr[i]];
    return { ...f, heads: arr };
  });
  // When picking a built-in field, default the label + bucket from the catalog
  const pickField = (i, key) => {
    const fld = meta.fields.find(x => x.key === key);
    setHead(i, { field_key: key, bucket: fld?.bucket || 'earning', label: form.heads[i].label || fld?.label || '' });
  };

  const saveTemplate = async (lockAfter = null) => {
    if (!form.name.trim()) { toast.error('Template name is required'); return; }
    if (!form.heads.length) { toast.error('Add at least one column (head)'); return; }
    for (const h of form.heads) {
      if (!h.label.trim()) { toast.error('Every column needs a name'); return; }
      if (h.source_type === 'field' && !h.field_key) { toast.error(`Pick a payroll field for "${h.label}"`); return; }
    }
    setSaving(true);
    try {
      const isNew = editing?.isNew;
      const url = isNew ? `${API_URL}/api/salary-templates` : `${API_URL}/api/salary-templates/${editing.template_id}`;
      const cleanHeads = form.heads.map(h => ({
        ...h,
        formula_percent: h.formula_percent === '' || h.formula_percent == null ? 0 : (parseFloat(h.formula_percent) || 0),
        default_amount: h.default_amount === '' || h.default_amount == null ? 0 : (parseFloat(h.default_amount) || 0),
      }));
      const locked = lockAfter === null ? form.locked : lockAfter;
      const res = await fetch(url, {
        method: isNew ? 'POST' : 'PUT', headers: getHeaders(), credentials: 'include',
        body: JSON.stringify({ ...form, heads: cleanHeads, locked }),
      });
      const d = await res.json();
      if (!res.ok) {
        const msg = Array.isArray(d.detail) ? (d.detail[0]?.msg || 'Invalid input') : (d.detail || 'Save failed');
        throw new Error(msg);
      }
      toast.success(locked ? 'Template finalised & locked' : (isNew ? 'Template saved as draft' : 'Template updated'));
      setEditing(null); setForm(null);
      load();
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  const toggleLock = async (t) => {
    try {
      const res = await fetch(`${API_URL}/api/salary-templates/${t.template_id}/${t.locked ? 'unlock' : 'lock'}`, {
        method: 'POST', headers: getHeaders(), credentials: 'include',
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Failed');
      toast.success(d.message || 'Done');
      load();
    } catch (e) { toast.error(e.message); }
  };

  const deleteTemplate = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"? This cannot be undone. Existing payroll is not affected; unmapped staff fall back to the standard sheet.`)) return;
    try {
      const res = await fetch(`${API_URL}/api/salary-templates/${t.template_id}`, { method: 'DELETE', headers: getHeaders(), credentials: 'include' });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Delete failed');
      toast.success('Template deleted');
      load();
    } catch (e) { toast.error(e.message); }
  };

  const openValues = async (t) => {
    setValuesFor(t); setGridHeads([]); setGridRows([]); setGridLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/salary-templates/${t.template_id}/employees`, { headers: getHeaders(), credentials: 'include' });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Failed to load staff');
      setGridHeads(d.heads || []);
      setGridRows((d.employees || []).map(e => ({ ...e, values: { ...(e.values || {}) } })));
    } catch (e) { toast.error(e.message); } finally { setGridLoading(false); }
  };
  const setGridValue = (empId, headId, val) =>
    setGridRows(rows => rows.map(r => r.employee_id === empId ? { ...r, values: { ...r.values, [headId]: val } } : r));

  const saveValues = async () => {
    setGridSaving(true);
    try {
      const payload = { values: {} };
      gridRows.forEach(r => {
        payload.values[r.employee_id] = {};
        gridHeads.forEach(h => {
          const v = r.values[h.head_id];
          if (v !== '' && v !== undefined && v !== null) payload.values[r.employee_id][h.head_id] = parseFloat(v) || 0;
        });
      });
      const res = await fetch(`${API_URL}/api/salary-templates/${valuesFor.template_id}/employee-values`, {
        method: 'PUT', headers: getHeaders(), credentials: 'include', body: JSON.stringify(payload),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Save failed');
      toast.success(d.message || 'Saved');
      setValuesFor(null);
    } catch (e) { toast.error(e.message); } finally { setGridSaving(false); }
  };

  const entName = (id) => (meta.entities.find(e => e.entity_id === id) || {}).name || id;

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto" data-testid="salary-templates-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Table2 className="w-6 h-6 text-emerald-600" /> Salary Sheet Templates
          </h1>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl">
            Build each institute its own salary sheet — add, rename or remove any column, set it to a
            fixed amount, a percentage of a base, a payroll field or Not Applicable, and watch the total
            update live. <b>Finalize &amp; Lock</b> a template to make it the sheet payroll uses. Staff whose
            institute has no locked template keep the standard SGI sheet.
          </p>
        </div>
        <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={openNew} data-testid="new-template-btn">
          <Plus className="w-4 h-4 mr-1.5" /> New Template
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-400 py-16 justify-center"><Loader2 className="w-5 h-5 animate-spin" /> Loading…</div>
      ) : templates.length === 0 ? (
        <div className="border-2 border-dashed rounded-xl p-12 text-center text-slate-400" data-testid="templates-empty">
          <Table2 className="w-10 h-10 mx-auto mb-3 opacity-40" />
          No templates yet. Create one, e.g. a <b>Gurumauli Services</b> sheet with its own heads.
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2" data-testid="templates-list">
          {templates.map(t => (
            <div key={t.template_id} className="rounded-xl border bg-white p-4 shadow-sm" data-testid={`template-card-${t.template_id}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="font-semibold text-slate-800">{t.name}</p>
                  {t.description ? <p className="text-xs text-slate-500 mt-0.5">{t.description}</p> : null}
                </div>
                {t.locked
                  ? <Badge className="bg-emerald-600 text-white" data-testid={`badge-locked-${t.template_id}`}><Lock className="w-3 h-3 mr-1" />Locked</Badge>
                  : <Badge variant="outline" className="border-amber-300 text-amber-600" data-testid={`badge-draft-${t.template_id}`}>Draft</Badge>}
              </div>
              <div className="flex flex-wrap gap-1 mt-3">
                {(t.entity_ids || []).length === 0
                  ? <Badge variant="outline" className="text-[11px] border-slate-300 text-slate-500">All institutes</Badge>
                  : (t.entity_ids || []).map(id => (
                    <Badge key={id} className="text-[11px] bg-indigo-50 text-indigo-700 border border-indigo-200">{entName(id)}</Badge>
                  ))}
                {(t.appointment_types || []).length === 0
                  ? <Badge variant="outline" className="text-[11px] border-slate-300 text-slate-400">All types</Badge>
                  : (t.appointment_types || []).map(a => (
                    <Badge key={a} variant="secondary" className="text-[11px]">{a}</Badge>
                  ))}
              </div>
              <p className="text-[11px] text-slate-400 mt-2">{(t.heads || []).length} column(s){!t.locked && ' · draft (not used for payroll until locked)'}</p>
              <div className="flex flex-wrap gap-2 mt-3">
                <Button size="sm" variant="outline" onClick={() => openEdit(t)} data-testid={`edit-template-${t.template_id}`}>
                  <Pencil className="w-3.5 h-3.5 mr-1" /> Edit columns
                </Button>
                <Button size="sm" variant="outline" onClick={() => openPreview(t.heads, t.name, t.template_id)} data-testid={`preview-template-${t.template_id}`}>
                  <Eye className="w-3.5 h-3.5 mr-1" /> Preview
                </Button>
                <Button size="sm" variant="outline" onClick={() => openValues(t)} data-testid={`values-template-${t.template_id}`}>
                  <Users className="w-3.5 h-3.5 mr-1" /> Per-employee amounts
                </Button>
                <Button size="sm" variant={t.locked ? 'outline' : 'default'} className={t.locked ? '' : 'bg-emerald-600 hover:bg-emerald-700 text-white'} onClick={() => toggleLock(t)} data-testid={`lock-template-${t.template_id}`}>
                  {t.locked ? <><Unlock className="w-3.5 h-3.5 mr-1" /> Unlock</> : <><Lock className="w-3.5 h-3.5 mr-1" /> Finalize &amp; Lock</>}
                </Button>
                <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700" onClick={() => deleteTemplate(t)} data-testid={`delete-template-${t.template_id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ---------------- Builder dialog ---------------- */}
      <Dialog open={!!form} onOpenChange={(v) => { if (!v && !saving) { setForm(null); setEditing(null); } }}>
        <DialogContent className="max-w-[95vw] sm:max-w-4xl sm:max-h-[92vh] overflow-y-auto" data-testid="template-builder-dialog">
          <DialogHeader>
            <DialogTitle>{editing?.isNew ? 'New Salary Template' : `Edit — ${editing?.name}`}</DialogTitle>
            <DialogDescription>Pick the institute(s) this sheet belongs to, then build its columns. Blank institute = all institutes; blank appointment type = all types.</DialogDescription>
          </DialogHeader>
          {form && (
            <div className="space-y-4">
              {form.locked && (
                <div className="rounded-md bg-amber-50 border border-amber-200 text-amber-800 text-xs px-3 py-2" data-testid="builder-locked-note">
                  This template is <b>Locked</b> and drives payroll. Saving keeps it locked. Use <b>Unlock</b> on its card first if you want to pause it while experimenting.
                </div>
              )}
              <div className="grid sm:grid-cols-3 gap-3">
                <div>
                  <Label>Template name *</Label>
                  <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    placeholder="e.g. Gurumauli Services" data-testid="template-name" />
                </div>
                <div>
                  <Label>Institute(s)</Label>
                  <MultiSelect
                    options={meta.entities.map(e => e.name)}
                    selected={form.entity_ids.map(id => entName(id))}
                    onChange={(names) => setForm(f => ({ ...f, entity_ids: names.map(n => (meta.entities.find(e => e.name === n) || {}).entity_id).filter(Boolean) }))}
                    placeholder="All institutes"
                    allLabel="All institutes"
                    emptyLabel="All institutes"
                    testId="template-entities"
                    emptyText="No institutes available"
                  />
                </div>
                <div>
                  <Label>Appointment type(s)</Label>
                  <MultiSelect
                    options={meta.appointment_types}
                    selected={form.appointment_types}
                    onChange={(v) => setForm(f => ({ ...f, appointment_types: v }))}
                    placeholder="All types"
                    allLabel="All appointment types"
                    emptyLabel="All types"
                    testId="template-appt-types"
                    emptyText="No appointment types in master data"
                  />
                </div>
              </div>
              <div>
                <Label>Description</Label>
                <Input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="Optional note" data-testid="template-description" />
              </div>

              <div className="border-t pt-3">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-sm text-slate-700">Columns (heads)</h3>
                  <Button size="sm" variant="outline" onClick={addHead} data-testid="add-head-btn">
                    <Plus className="w-3.5 h-3.5 mr-1" /> Add column
                  </Button>
                </div>
                <p className="text-[11px] text-slate-400 mb-3">GROSS, TOTAL DEDUCTION and NET PAYABLE columns are added automatically.</p>
                <div className="rounded-lg border border-indigo-200 bg-indigo-50/60 p-3 mb-3" data-testid="builder-emp-picker">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                    <Label className="text-[12px] font-semibold text-indigo-800 whitespace-nowrap">Check against a real employee</Label>
                    <Input className="bg-white h-9 sm:max-w-[200px]" placeholder="Search staff by name…"
                      value={empQuery} onChange={e => setEmpQuery(e.target.value)} data-testid="builder-emp-search" />
                    <Select value={builderEmp ? builderEmp.employee_id : '__demo'} onValueChange={pickBuilderEmp}>
                      <SelectTrigger className="bg-white h-9 sm:max-w-[260px]" data-testid="builder-emp-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__demo">Demo staff (sample figures)</SelectItem>
                        {pickerOptions(builderEmp ? builderEmp.employee_id : '__demo').map(o => (
                          <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <p className="text-[11px] text-indigo-700/80 mt-1.5">
                    {builderEmp
                      ? <>Each column's <b>Live</b> value below now uses <b>{builderEmp.name}</b>'s actual salary, so you can confirm the sheet reads their figures correctly before locking.</>
                      : <>Pick a staff member to pull their <b>real salary</b> into every column's live value — makes it easy to verify the template is correct.</>}
                  </p>
                </div>
                <div className="space-y-2">
                  {form.heads.map((h, i) => (
                    <div key={i} className="rounded-lg border bg-slate-50 p-3" data-testid={`head-row-${i}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <GripVertical className="w-4 h-4 text-slate-300" />
                        <Input className="flex-1 bg-white" value={h.label} placeholder="Column name, e.g. Washing Allowance"
                          onChange={e => setHead(i, { label: e.target.value })} data-testid={`head-label-${i}`} />
                        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => moveHead(i, -1)} data-testid={`head-up-${i}`}><ArrowUp className="w-3.5 h-3.5" /></Button>
                        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => moveHead(i, 1)} data-testid={`head-down-${i}`}><ArrowDown className="w-3.5 h-3.5" /></Button>
                        <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600" onClick={() => removeHead(i)} data-testid={`head-remove-${i}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                      </div>
                      <div className="grid sm:grid-cols-3 gap-2">
                        <div>
                          <Label className="text-[11px] text-slate-500">Bucket</Label>
                          <Select value={h.bucket} onValueChange={(v) => setHead(i, { bucket: v })}>
                            <SelectTrigger className="bg-white h-9" data-testid={`head-bucket-${i}`}><SelectValue /></SelectTrigger>
                            <SelectContent>{BUCKETS.map(b => <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-[11px] text-slate-500">Value source</Label>
                          <Select value={h.source_type} onValueChange={(v) => setHead(i, { source_type: v })}>
                            <SelectTrigger className="bg-white h-9" data-testid={`head-source-${i}`}><SelectValue /></SelectTrigger>
                            <SelectContent>{SOURCES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <div>
                          {h.source_type === 'field' && (
                            <>
                              <Label className="text-[11px] text-slate-500">Field</Label>
                              <Select value={h.field_key || ''} onValueChange={(v) => pickField(i, v)}>
                                <SelectTrigger className="bg-white h-9" data-testid={`head-field-${i}`}><SelectValue placeholder="Pick field" /></SelectTrigger>
                                <SelectContent>{meta.fields.map(f => <SelectItem key={f.key} value={f.key}>{f.label}</SelectItem>)}</SelectContent>
                              </Select>
                            </>
                          )}
                          {h.source_type === 'formula' && (
                            <div className="grid grid-cols-2 gap-1">
                              <div>
                                <Label className="text-[11px] text-slate-500">% of</Label>
                                <Select value={h.formula_base || 'gross_earned'} onValueChange={(v) => setHead(i, { formula_base: v })}>
                                  <SelectTrigger className="bg-white h-9" data-testid={`head-base-${i}`}><SelectValue /></SelectTrigger>
                                  <SelectContent>{meta.formula_bases.map(b => <SelectItem key={b.key} value={b.key}>{b.label}</SelectItem>)}</SelectContent>
                                </Select>
                              </div>
                              <div>
                                <Label className="text-[11px] text-slate-500">Percent</Label>
                                <Input type="number" step="0.01" className="bg-white h-9" value={h.formula_percent}
                                  onChange={e => setHead(i, { formula_percent: e.target.value })} data-testid={`head-percent-${i}`} />
                              </div>
                            </div>
                          )}
                          {(h.source_type === 'fixed' || h.source_type === 'manual') && (
                            <>
                              <Label className="text-[11px] text-slate-500">Default amount</Label>
                              <Input type="number" step="1" className="bg-white h-9" value={h.default_amount}
                                onChange={e => setHead(i, { default_amount: e.target.value })} data-testid={`head-default-${i}`} />
                            </>
                          )}
                          {h.source_type === 'none' && (
                            <p className="text-[11px] text-slate-400 mt-5 italic">Hidden from the salary sheet.</p>
                          )}
                        </div>
                      </div>
                      {(h.source_type === 'fixed' || h.source_type === 'manual') && h.bucket === 'earning' && (
                        <label className="flex items-center gap-2 text-[11px] text-slate-500 mt-2 cursor-pointer">
                          <Switch checked={!!h.prorate} onCheckedChange={(v) => setHead(i, { prorate: v })} data-testid={`head-prorate-${i}`} />
                          Prorate by present / payable days
                        </label>
                      )}
                      {h.source_type !== 'none' && (
                        <p className="text-[11px] text-slate-500 mt-2" data-testid={`head-live-${i}`}>
                          Live ({builderEmp ? builderEmp.name : 'demo'}): <b className="text-slate-700">₹{money(liveValue(h, builderFv, !!builderFv))}</b>
                          {h.source_type === 'formula' && (
                            <span className="text-slate-400"> — {h.formula_percent || 0}% of {(meta.formula_bases.find(b => b.key === h.formula_base) || {}).label || h.formula_base}</span>
                          )}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
                <div className="mt-3 rounded-lg bg-slate-900 text-white px-4 py-2.5 flex flex-wrap items-center gap-x-6 gap-y-1 text-xs" data-testid="builder-live-summary">
                  {(() => {
                    const vis = form.heads.filter(h => h.source_type !== 'none' && (h.label || '').trim());
                    const g = vis.filter(h => h.bucket === 'earning').reduce((s, h) => s + (liveValue(h, builderFv, !!builderFv) || 0), 0);
                    const d = vis.filter(h => h.bucket === 'deduction').reduce((s, h) => s + (liveValue(h, builderFv, !!builderFv) || 0), 0);
                    return (<>
                      <span className="text-slate-300">Live {builderEmp ? `(${builderEmp.name})` : 'demo'} (updates instantly):</span>
                      <span>GROSS <b className="text-emerald-300">₹{money(g)}</b></span>
                      <span>DEDUCTIONS <b className="text-rose-300">₹{money(d)}</b></span>
                      <span>NET PAYABLE <b className="text-white">₹{money(g - d)}</b></span>
                    </>);
                  })()}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setForm(null); setEditing(null); }} disabled={saving}>Cancel</Button>
            <Button variant="outline" onClick={() => openPreview(form.heads, form.name || 'Salary Sheet Preview', editing?.isNew ? null : editing?.template_id, builderEmp ? builderEmp.employee_id : '__demo')} disabled={saving} data-testid="preview-builder-btn">
              <Eye className="w-4 h-4 mr-1.5" /> Preview
            </Button>
            <Button variant="outline" onClick={() => saveTemplate(false)} disabled={saving} data-testid="save-draft-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />} Save as Draft
            </Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => saveTemplate(true)} disabled={saving} data-testid="save-lock-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Lock className="w-4 h-4 mr-1.5" />} Finalize &amp; Lock
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---------------- Per-employee amounts dialog ---------------- */}
      <Dialog open={!!valuesFor} onOpenChange={(v) => { if (!v && !gridSaving) setValuesFor(null); }}>
        <DialogContent className="max-w-[95vw] sm:max-w-4xl sm:max-h-[92vh] overflow-y-auto" data-testid="template-values-dialog">
          <DialogHeader>
            <DialogTitle>Per-employee amounts — {valuesFor?.name}</DialogTitle>
            <DialogDescription>Enter the fixed/manual amounts for each staff member. Field &amp; formula columns compute automatically and are not shown here.</DialogDescription>
          </DialogHeader>
          {gridLoading ? (
            <div className="flex items-center gap-2 text-slate-400 py-10 justify-center"><Loader2 className="w-5 h-5 animate-spin" /> Loading staff…</div>
          ) : gridHeads.length === 0 ? (
            <p className="text-sm text-slate-500 py-6 text-center">This template has no fixed/manual columns — nothing to enter per employee.</p>
          ) : gridRows.length === 0 ? (
            <p className="text-sm text-amber-600 py-6 text-center">No active staff match this template's appointment type(s) yet.</p>
          ) : (
            <div className="overflow-x-auto border rounded-lg">
              <table className="w-full text-sm" data-testid="values-grid">
                <thead>
                  <tr className="bg-slate-50 border-b">
                    <th className="text-left px-3 py-2 font-semibold sticky left-0 bg-slate-50">Employee</th>
                    {gridHeads.map(h => <th key={h.head_id} className="text-right px-3 py-2 font-semibold whitespace-nowrap">{h.label}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {gridRows.map(r => (
                    <tr key={r.employee_id} className="border-b hover:bg-slate-50">
                      <td className="px-3 py-1.5 sticky left-0 bg-white">
                        <span className="font-medium">{r.name}</span>
                        <span className="block text-[10px] text-slate-400">{r.designation}{r.department ? ` · ${r.department}` : ''}</span>
                      </td>
                      {gridHeads.map(h => (
                        <td key={h.head_id} className="px-2 py-1.5">
                          <Input type="number" step="1" className="h-8 w-28 text-right ml-auto"
                            value={r.values[h.head_id] ?? ''}
                            placeholder={String(h.default_amount ?? 0)}
                            onChange={e => setGridValue(r.employee_id, h.head_id, e.target.value)}
                            data-testid={`grid-${r.employee_id}-${h.head_id}`} />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setValuesFor(null)} disabled={gridSaving}>Cancel</Button>
            {gridHeads.length > 0 && gridRows.length > 0 && (
              <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={saveValues} disabled={gridSaving} data-testid="save-values-btn">
                {gridSaving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />} Save Amounts
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* ---------------- Preview dialog (demo data) ---------------- */}
      <Dialog open={previewOpen} onOpenChange={(v) => { if (!v) { setPreviewOpen(false); setPreviewData(null); } }}>
        <DialogContent className="max-w-[95vw] lg:max-w-6xl max-h-[92vh] overflow-y-auto" data-testid="template-preview-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Eye className="w-5 h-5 text-emerald-600" /> {previewTitle}</DialogTitle>
            <DialogDescription>GROSS, TOTAL DEDUCTION and NET are calculated automatically. Switch to a real staff member to confirm the sheet reads their actual salary before finalising.</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 border rounded-lg bg-slate-50 px-3 py-2" data-testid="preview-emp-picker">
            <Label className="text-[12px] font-semibold text-slate-700 whitespace-nowrap">Preview with</Label>
            <Input className="bg-white h-9 sm:max-w-[200px]" placeholder="Search staff by name…"
              value={empQuery} onChange={e => setEmpQuery(e.target.value)} data-testid="preview-emp-search" />
            <Select value={previewEmp} onValueChange={changePreviewEmp}>
              <SelectTrigger className="bg-white h-9 sm:max-w-[280px]" data-testid="preview-emp-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__demo">Demo staff (3 sample profiles)</SelectItem>
                {pickerOptions(previewEmp).map(o => (
                  <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {previewData?.source === 'employees' && <span className="text-[11px] text-emerald-700">Showing real salary figures</span>}
          </div>
          {previewLoading ? (
            <div className="flex items-center gap-2 text-slate-400 py-12 justify-center"><Loader2 className="w-5 h-5 animate-spin" /> Building preview…</div>
          ) : !previewData ? null : (() => {
            const info = previewData.columns.filter(c => c.bucket === 'info');
            const earns = previewData.columns.filter(c => c.bucket === 'earning');
            const deds = previewData.columns.filter(c => c.bucket === 'deduction');
            const money = (n) => Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
            const sumFor = (row, cols) => cols.reduce((s, c) => s + Number(row.values[c.head_id] || 0), 0);
            const totals = {};
            [...info, ...earns, ...deds].forEach(c => { totals[c.head_id] = previewData.rows.reduce((s, r) => s + Number(r.values[c.head_id] || 0), 0); });
            let gGross = 0, gDed = 0, gNet = 0;
            return (
              <div className="overflow-x-auto border rounded-lg" data-testid="preview-table-wrap">
                <table className="text-xs border-collapse" data-testid="preview-table">
                  <thead>
                    <tr className="bg-slate-100">
                      <th className="border px-2 py-1.5 font-semibold text-left sticky left-0 bg-slate-100" colSpan={3}>Employee</th>
                      {info.map(c => <th key={c.head_id} className="border px-2 py-1.5 font-semibold bg-slate-100">{c.label}</th>)}
                      {earns.length > 0 && <th className="border px-2 py-1 font-bold bg-emerald-50 text-emerald-800" colSpan={earns.length + 1}>EARNINGS</th>}
                      {deds.length > 0 && <th className="border px-2 py-1 font-bold bg-rose-50 text-rose-800" colSpan={deds.length + 1}>DEDUCTIONS</th>}
                      <th className="border px-2 py-1 font-bold bg-slate-200">NET</th>
                    </tr>
                    <tr className="bg-slate-50">
                      <th className="border px-2 py-1.5 font-semibold text-left">#</th>
                      <th className="border px-2 py-1.5 font-semibold text-left">Name</th>
                      <th className="border px-2 py-1.5 font-semibold text-left">Designation</th>
                      {info.map(c => <th key={c.head_id} className="border px-2 py-1.5 whitespace-nowrap">{c.label}</th>)}
                      {earns.map(c => <th key={c.head_id} className="border px-2 py-1.5 whitespace-nowrap text-right">{c.label}</th>)}
                      {earns.length > 0 && <th className="border px-2 py-1.5 whitespace-nowrap text-right font-bold">GROSS</th>}
                      {deds.map(c => <th key={c.head_id} className="border px-2 py-1.5 whitespace-nowrap text-right">{c.label}</th>)}
                      {deds.length > 0 && <th className="border px-2 py-1.5 whitespace-nowrap text-right font-bold">TOTAL DED.</th>}
                      <th className="border px-2 py-1.5 whitespace-nowrap text-right font-bold">NET PAYABLE</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewData.rows.map((r, idx) => {
                      const gross = sumFor(r, earns), ded = sumFor(r, deds), net = gross - ded;
                      gGross += gross; gDed += ded; gNet += net;
                      return (
                        <tr key={idx} className="hover:bg-slate-50">
                          <td className="border px-2 py-1 text-center">{idx + 1}</td>
                          <td className="border px-2 py-1 whitespace-nowrap font-medium">{r.name}</td>
                          <td className="border px-2 py-1 whitespace-nowrap text-slate-500">{r.designation}</td>
                          {info.map(c => <td key={c.head_id} className="border px-2 py-1 text-center">{money(r.values[c.head_id])}</td>)}
                          {earns.map(c => <td key={c.head_id} className="border px-2 py-1 text-right">{money(r.values[c.head_id])}</td>)}
                          {earns.length > 0 && <td className="border px-2 py-1 text-right font-semibold bg-emerald-50/50">{money(gross)}</td>}
                          {deds.map(c => <td key={c.head_id} className="border px-2 py-1 text-right">{money(r.values[c.head_id])}</td>)}
                          {deds.length > 0 && <td className="border px-2 py-1 text-right font-semibold bg-rose-50/50">{money(ded)}</td>}
                          <td className="border px-2 py-1 text-right font-bold bg-slate-100">{money(net)}</td>
                        </tr>
                      );
                    })}
                    <tr className="bg-slate-100 font-bold">
                      <td className="border px-2 py-1 text-center" colSpan={3}>TOTAL</td>
                      {info.map(c => <td key={c.head_id} className="border px-2 py-1 text-center">—</td>)}
                      {earns.map(c => <td key={c.head_id} className="border px-2 py-1 text-right">{money(totals[c.head_id])}</td>)}
                      {earns.length > 0 && <td className="border px-2 py-1 text-right">{money(gGross)}</td>}
                      {deds.map(c => <td key={c.head_id} className="border px-2 py-1 text-right">{money(totals[c.head_id])}</td>)}
                      {deds.length > 0 && <td className="border px-2 py-1 text-right">{money(gDed)}</td>}
                      <td className="border px-2 py-1 text-right">{money(gNet)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            );
          })()}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setPreviewOpen(false); setPreviewData(null); }}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
